import { Label } from "../types/schema";
import { getApiClient } from './api-client';

/**
 * Label colors palette
 */
export const LABEL_COLORS = [
  "#ef4444", // red
  "#f97316", // orange
  "#f59e0b", // amber
  "#eab308", // yellow
  "#84cc16", // lime
  "#22c55e", // green
  "#10b981", // emerald
  "#14b8a6", // teal
  "#06b6d4", // cyan
  "#0ea5e9", // sky
  "#3b82f6", // blue
  "#6366f1", // indigo
  "#8b5cf6", // violet
  "#a855f7", // purple
  "#d946ef", // fuchsia
  "#ec4899", // pink
  "#f43f5e", // rose
];

/**
 * Label Creation Request DTO
 */
export interface CreateLabelRequest {
  name: string;
  color: string;
  description?: string;
}

/**
 * Label Service
 * Handles all label-related API calls
 */
class LabelService {
  /**
   * Get all labels in the workspace
   * @returns List of labels
   */
  async getWorkspaceLabels(workspaceId: number): Promise<Label[]> {
    const client = getApiClient();
    return client.get<Label[]>(`/workspaces/${workspaceId}/labels`);
  }

  /**
   * Create a new label
   * @param request Label creation data
   * @returns Created label
   */
  async createLabel(workspaceId: number, request: CreateLabelRequest): Promise<Label> {
    const client = getApiClient();
    return client.post<Label>(`/workspaces/${workspaceId}/labels`, request);
  }

  /**
   * Update a label
   * @param id Label ID
   * @param request Update data
   * @returns Updated label
   */
  async updateLabel(
    workspaceId: number,
    id: number,
    request: Partial<CreateLabelRequest>
  ): Promise<Label> {
    const client = getApiClient();
    return client.patch<Label>(`/workspaces/${workspaceId}/labels/${id}`, request);
  }

  /**
   * Delete a label
   * @param id Label ID
   */
  async deleteLabel(workspaceId: number, id: number): Promise<void> {
    const client = getApiClient();
    return client.delete<void>(`/workspaces/${workspaceId}/labels/${id}`);
  }

  /**
   * Search labels by keyword
   * @param keyword Search keyword
   * @returns Filtered list of labels
   */
  async searchLabels(workspaceId: number, keyword: string): Promise<Label[]> {
    const client = getApiClient();
    return client.get<Label[]>(`/workspaces/${workspaceId}/labels/search?q=${encodeURIComponent(keyword)}`);
  }
}

// Export singleton instance
export const labelService = new LabelService();
